/**
 * Retrieve the list of files for further process
 * @param {FileCollection} fileCollection
 * @param {object} [options={}]
 * @param {number[]} [options.processingNumbers] - processing number to select, default the smallest number
 * @param {number[]} [options.experimentNumbers] - experiment number to select, default all
 * @param {boolean} [options.keep1D=true] - should we keep 1D spectra
 * @param {boolean} [options.keep2D=true] - should we keep 2D spectra
 * @param {boolean} [options.onlyFirstProcessedData=true] - should we take only the first processed data (procno).
 */

export function groupByExperiments(fileCollection, options = {}) {
  let {
    processingNumbers,
    experimentNumbers,
    dataSelection = 'both',
    keep1D = true,
    keep2D = true,
    onlyFirstProcessedData = true,
  } = options;
  const experiments = {};
  const fileList = fileCollection.files.map((file) => file.relativePath);
  for (let file of fileCollection) {
    let procno;
    let expno;
    let experiment;
    let name;
    let id;
    const parts = file.relativePath.split('/');
    if (file.relativePath.match(/\/pdata\/\d+\//)) {
      const pdataIndex = parts.indexOf('pdata');
      procno = Number(parts[pdataIndex + 1]);
      expno = Number(parts[pdataIndex - 1]);
      expno = Number.isInteger(expno) ? expno : 0;
      name = parts[pdataIndex - 2] || parts[pdataIndex - 1];
      id = parts.slice(0, -3).join('/');
    } else if (isRawOrAcquisitionFile(file, fileList)) {
      const firstLevel = parts.indexOf(file.name);
      expno = Number(parts[firstLevel - 1]);
      expno = Number.isInteger(expno) ? expno : 0;
      name = parts[firstLevel - 2] || parts[firstLevel - 1];
      id = parts.slice(0, -1).join('/');
    } else {
      continue;
    }

    if (experimentNumbers && !experimentNumbers.includes(expno)) {
      continue;
    }

    if (!experiments[id]) {
      experiments[id] = {
        name,
        expno,
        processedData: {},
        fileCollectionItems: [],
      };
    }
    experiment = experiments[id];
    if (procno) {
      if (!experiment.processedData[procno]) {
        experiment.processedData[procno] = {
          fileCollectionItems: [],
          name,
          expno,
          procno,
        };
      }
      const processedData = experiment.processedData[procno];
      processedData.fileCollectionItems.push(file); // check if it can be moved into the conditional block
      if (file.name.match(/^(1r|1i|2rr|procs|proc2s)$/)) {
        processedData[file.name] = file;
      }
      if (file.name === '2rr') {
        processedData.is2D = true;
        processedData.isFT = true;
      }
      if (file.name === '1r') {
        processedData.is1D = true;
        processedData.isFT = true;
      }
    } else {
      experiment.fileCollectionItems.push(file); // check if it can be moved into the conditional block
      if (file.name.match(/^(ser|fid|acqus|acqu2s)$/)) {
        experiment[file.name] = file;
      }
      if (file.name === 'ser') {
        experiment.is2D = true;
        experiment.isFID = true;
      }
      if (file.name === 'fid') {
        experiment.is1D = true;
        experiment.isFID = true;
      }
    }

    if (file.name === 'QuantFactorSample.xml') {
      experiment.quantFactorSample = file;
    }
  }
  if (processingNumbers) {
    // we need to deal with the list of processingNumbers that is specified
    for (const key in experiments) {
      const processedData = experiments[key].processedData;
      const newProcessedData = {};
      for (let key in processedData) {
        if (!processingNumbers.includes(parseInt(key, 10))) continue;
        newProcessedData[key] = processedData[key];
      }
      experiments[key].processedData = newProcessedData;
    }
  } else if (onlyFirstProcessedData) {
    // take the smallest processing and remove all the other
    for (const key in experiments) {
      const processedData = experiments[key].processedData;
      const firstProcessedNumber = Object.keys(processedData).sort(
        (a, b) => Number(a) - Number(b),
      )[0];
      if (firstProcessedNumber !== undefined) {
        experiments[key].processedData = {
          firstProcessedNumber: processedData[firstProcessedNumber],
        };
      }
    }
  }
  // We convert the object to an array
  // and before filtering we will move all the processedData
  let experimentsArray = [];

  for (let key in experiments) {
    const { processedData: processed, ...experiment } = { ...experiments[key] };

    if (shouldAddRawData(experiments[key], dataSelection)) {
      const processedKeys = Object.keys(processed);
      if (processedKeys.length > 0) {
        const firstProcessed = processed[processedKeys[0]];
        if (firstProcessed.procs) {
          experiment.fileCollectionItems.push(firstProcessed.procs);
          experiment.procs = firstProcessed.procs;
        }
        if (firstProcessed.proc2s) {
          experiment.fileCollectionItems.push(firstProcessed.proc2s);
          experiment.proc2s = firstProcessed.proc2s;
        }
      }
      experimentsArray.push(experiment);
    }

    const hasRawData = 'ser' in experiment || 'fid' in experiment;
    if (
      dataSelection === 'fid' ||
      (dataSelection === 'preferFID' && hasRawData)
    ) {
      continue;
    }
    for (let processedKey in processed) {
      const oneProcessed = processed[processedKey];
      if (oneProcessed['1r'] || oneProcessed['2rr']) {
        if (experiment.acqus) {
          oneProcessed.fileCollectionItems.push(experiment.acqus);
        }
        if (experiment.acqu2s) {
          oneProcessed.fileCollectionItems.push(experiment.acqu2s);
        }
        experimentsArray.push({
          acqus: experiment.acqus,
          acqu2s: experiment.acqu2s,
          ...oneProcessed,
        });
      }
    }
  }
  // we can not easily filter
  if (!keep1D) {
    experimentsArray = experimentsArray.filter((item) => !item.is1D);
  }
  if (!keep2D) {
    experimentsArray = experimentsArray.filter((item) => !item.is2D);
  }

  return experimentsArray;
}

function isRawOrAcquisitionFile(file, fileList) {
  return (
    file.name.match(/^(ser|acqus|acqu2s|QuantFactorSample.xml)$/) ||
    (file.name.match(/^(fid)$/) &&
      fileList.includes(file.relativePath.replace(/fid$/, 'acqus')))
  );
}

function shouldAddRawData(experiment, dataSelection) {
  const { processedData: processed } = experiment;
  const processedKeys = Object.keys(processed);
  const hasProcessData =
    processedKeys.length > 0 &&
    ('1r' in processed[processedKeys[0]] ||
      '1i' in processed[processedKeys[0]]);

  const hasRawData = 'ser' in experiment || 'fid' in experiment;
  const notPreferFt = dataSelection !== 'ft' && dataSelection !== 'preferFT';
  const preferFtWithoutProcessedData =
    dataSelection === 'preferFT' && !hasProcessData;

  return hasRawData && (notPreferFt || preferFtWithoutProcessedData);
}
